import { environment } from '../environment/environment';
import { useAuthStore } from '../stores';

const getUserId = (): string => {
  const user = useAuthStore.getState().user;
  const id = (user as any)?.id;
  if (!id) throw new Error('User not authenticated');
  return id;
};

export interface ShareUser {
  id: string;
  name: string | null;
  email: string;
}

export interface SharedEntry {
  id: string;
  shared_with: string;
  shared_with_user: ShareUser;
  permission: 'view' | 'edit';
  created_at: string;
}

export interface ListItem {
  id: string;
  list_name: string;
  description: string | null;
  list_type: 'brand' | 'store';
  type: 'brand' | 'store';
  poi_id: string | null;
  brand_name: string | null;
  user_id: string;
  created_at: string;
  updated_at: string;
}

export interface StorePoiDetail {
  id: string;
  site: string;
  address: string;
  brand_name: string;
  purchek_install_date: string | null;
  containment_install_date: string | null;
  cart_manager_install_date: string | null;
  subscription_start_date: string | null;
  paired_wheel_install_date: string | null;
  geo: {
    city: string;
    state_code: string;
    zipcode: string;
    total_crime_incidents: number;
    no_of_hot_days: number;
    no_of_cold_days: number;
    penalty_for_cart_owner: boolean;
  } | null;
  nearby_penetration: {
    purchek_customer_count: number;
    penetration_purchek: number;
    containment_customer_count: number;
    penetration_containment: number;
    penetration_paired_wheel: number;
    plaza_cm_customer_count: number;
  } | null;
}

export interface BrandDetail {
  name: string;
  total_stores: number;
  occupied_stores: number;
}

export interface ListSummary {
  list_id?: string;
  list_name: string;
  description: string | null;
  list_type: 'brand' | 'store';
  count: number;
  items: ListItem[];
  storeDetails?: Map<string, StorePoiDetail>;
  brandDetails?: Map<string, BrandDetail>;
  sharedCount?: number;
  isShared?: boolean;
  sharedByName?: string;
  sharedByEmail?: string;
  sharedPermission?: 'view' | 'edit';
  org_access?: 'view' | 'edit' | null;
}

const graphqlFetch = async (query: string, variables?: Record<string, any>) => {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(environment.graphqlEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ query, variables }),
  });

  const result = await response.json();
  if (result.errors) {
    console.error('GraphQL error:', result.errors);
    throw new Error(result.errors[0]?.message || 'GraphQL error');
  }
  return result.data;
};

export const createList = async (
  listName: string,
  listType: 'brand' | 'store',
  description?: string,
): Promise<{ success: boolean; listId?: string; error?: string }> => {
  try {
    const userId = getUserId();
    const data = await graphqlFetch(`
      mutation CreateList($list_name: String!, $list_type: String!, $description: String, $type: String!, $user_id: uuid!) {
        insert_app_list_one(object: {
          list_name: $list_name,
          list_type: $list_type,
          description: $description,
          type: $type,
          brand_name: "__placeholder__",
          user_id: $user_id
        }) {
          id
        }
      }
    `, {
      list_name: listName,
      list_type: listType,
      description: description || null,
      type: listType,
      user_id: userId,
    });

    return { success: true, listId: data?.insert_app_list_one?.id };
  } catch (error: any) {
    const msg = error?.message || '';
    if (msg.includes('Uniqueness violation') || msg.includes('unique constraint')) {
      return { success: false, error: 'A list with this name already exists.' };
    }
    console.error('Error creating list:', error);
    return { success: false, error: 'Failed to create list.' };
  }
};

const STORE_POI_FIELDS = `
  id
  site
  address
  brand_name
  purchek_install_date
  containment_install_date
  cart_manager_install_date
  subscription_start_date
  paired_wheel_install_date
  geo {
    city
    state_code
    zipcode
    total_crime_incidents
    no_of_hot_days
    no_of_cold_days
    penalty_for_cart_owner
  }
  nearby_penetration {
    purchek_customer_count
    penetration_purchek
    containment_customer_count
    penetration_containment
    penetration_paired_wheel
    plaza_cm_customer_count
  }
  updated_store_ids(order_by: {created_at: desc}, limit: 1) {
    store_id
    created_at
  }
`;

const groupListItems = (items: (ListItem & { org_access?: string | null })[]) => {
  const grouped = new Map<string, { items: ListItem[]; description: string | null; list_type: 'brand' | 'store'; org_access?: string | null; list_id?: string }>();
  items.forEach(item => {
    const existing = grouped.get(item.list_name);
    if (existing) {
      if (item.brand_name === '__placeholder__') {
        existing.list_type = item.list_type;
        existing.list_id = item.id;
        if (item.description) existing.description = item.description;
        if (item.org_access !== undefined) existing.org_access = item.org_access;
      } else {
        existing.items.push(item);
      }
    } else {
      grouped.set(item.list_name, {
        items: item.brand_name === '__placeholder__' ? [] : [item],
        description: item.description,
        list_type: item.list_type,
        org_access: item.org_access,
        list_id: item.brand_name === '__placeholder__' ? item.id : undefined,
      });
    }
  });
  return grouped;
};

export const fetchAllLists = async (): Promise<{ myLists: ListSummary[]; sharedLists: ListSummary[] }> => {
  try {
    const userId = getUserId();
    const data = await graphqlFetch(`
      query GetAllLists($userId: uuid!) {
        my_items: app_list(where: { user_id: { _eq: $userId } }, order_by: { created_at: desc }) {
          id
          list_name
          description
          list_type
          org_access
          type
          poi_id
          brand_name
          user_id
          created_at
          updated_at
        }
        shared_entries: app_shared_list(where: { shared_with: { _eq: $userId } }, order_by: { created_at: desc }) {
          list_name
          permission
          shared_by
          shared_by_user {
            id
            name
            email
          }
        }
        my_shared_counts: app_shared_list(where: { shared_by: { _eq: $userId } }) {
          list_name
        }
      }
    `, { userId });

    const myItems: ListItem[] = data.my_items || [];
    const myGrouped = groupListItems(myItems);

    const sharedCountMap = new Map<string, number>();
    (data.my_shared_counts || []).forEach((entry: { list_name: string }) => {
      sharedCountMap.set(entry.list_name, (sharedCountMap.get(entry.list_name) || 0) + 1);
    });

    const allPoiIds: string[] = [];
    const allBrandNames: string[] = [];
    myItems.forEach(item => {
      if (item.brand_name === '__placeholder__') return;
      if (item.type === 'store' && item.poi_id) allPoiIds.push(item.poi_id);
      if (item.type === 'brand' && item.brand_name) allBrandNames.push(item.brand_name);
    });

    const sharedEntries = data.shared_entries || [];
    let sharedLists: ListSummary[] = [];
    let sharedItems: ListItem[] = [];

    if (sharedEntries.length > 0) {
      const sharerMap = new Map<string, { id: string; name: string; email: string; permission: 'view' | 'edit' }>();
      const sharerListNames: string[] = [];
      sharedEntries.forEach((e: any) => {
        sharerMap.set(`${e.shared_by}::${e.list_name}`, {
          id: e.shared_by_user?.id || e.shared_by,
          name: e.shared_by_user?.name || e.shared_by_user?.email?.split('@')[0] || 'Unknown',
          email: e.shared_by_user?.email || '',
          permission: e.permission || 'view',
        });
        sharerListNames.push(e.list_name);
      });

      const listNames = Array.from(new Set<string>(sharerListNames));
      const sharerIds = Array.from(new Set<string>(sharedEntries.map((e: any) => e.shared_by)));

      const listData = await graphqlFetch(`
        query GetSharedListItems($listNames: [String!]!, $sharerIds: [uuid!]!) {
          app_list(
            where: { list_name: { _in: $listNames }, user_id: { _in: $sharerIds } },
            order_by: { created_at: desc }
          ) {
            id
            list_name
            description
            list_type
            type
            poi_id
            brand_name
            user_id
            created_at
            updated_at
          }
        }
      `, { listNames, sharerIds });

      sharedItems = listData.app_list || [];
      sharedItems.forEach(item => {
        if (item.brand_name === '__placeholder__') return;
        if (item.type === 'store' && item.poi_id) allPoiIds.push(item.poi_id);
        if (item.type === 'brand' && item.brand_name) allBrandNames.push(item.brand_name);
      });

      const sharedGrouped = new Map<string, { items: ListItem[]; description: string | null; list_type: 'brand' | 'store'; userId: string; list_id?: string }>();
      sharedItems.forEach(item => {
        const key = `${item.user_id}::${item.list_name}`;
        const existing = sharedGrouped.get(key);
        if (existing) {
          if (item.brand_name === '__placeholder__') {
            existing.list_type = item.list_type;
            existing.list_id = item.id;
            if (item.description) existing.description = item.description;
          } else {
            existing.items.push(item);
          }
        } else {
          sharedGrouped.set(key, {
            items: item.brand_name === '__placeholder__' ? [] : [item],
            description: item.description,
            list_type: item.list_type,
            userId: item.user_id,
            list_id: item.brand_name === '__placeholder__' ? item.id : undefined,
          });
        }
      });

      sharedLists = Array.from(sharedGrouped.entries())
        .filter(([key]) => sharerMap.has(key))
        .map(([key, d]) => {
          const sharer = sharerMap.get(key)!;
          return {
            list_id: d.list_id,
            list_name: d.items[0]?.list_name || key.split('::')[1],
            description: d.description,
            list_type: d.list_type,
            count: d.items.length,
            items: d.items,
            isShared: true,
            sharedByName: sharer.name,
            sharedByEmail: sharer.email,
            sharedPermission: sharer.permission,
          };
        });
    }

    let storeDetailsMap: Map<string, StorePoiDetail> | undefined;
    const uniquePoiIds = Array.from(new Set(allPoiIds));
    if (uniquePoiIds.length > 0) {
      const poiData = await graphqlFetch(`
        query GetStorePoiDetails($poiIds: [String!]!) {
          udm_poi(where: { id: { _in: $poiIds }, closed_on: { _is_null: true } }) {
            ${STORE_POI_FIELDS}
          }
        }
      `, { poiIds: uniquePoiIds });

      storeDetailsMap = new Map();
      (poiData.udm_poi || []).forEach((poi: StorePoiDetail) => {
        storeDetailsMap!.set(poi.id, poi);
      });
    }

    let brandDetailsMap: Map<string, BrandDetail> | undefined;
    const uniqueBrandNames = Array.from(new Set(allBrandNames));
    if (uniqueBrandNames.length > 0) {
      const brandData = await graphqlFetch(`
        query GetBrandDetails($names: [String!]!) {
          udm_brand(where: { name: { _in: $names } }) {
            name
            total_stores: pois_aggregate(where: { closed_on: { _is_null: true } }) {
              aggregate { count }
            }
            occupied_stores: pois_aggregate(where: { _and: [{ closed_on: { _is_null: true } }, { customer_id: { _is_null: false } }] }) {
              aggregate { count }
            }
          }
        }
      `, { names: uniqueBrandNames });

      brandDetailsMap = new Map();
      (brandData.udm_brand || []).forEach((b: any) => {
        brandDetailsMap!.set(b.name, {
          name: b.name,
          total_stores: b.total_stores?.aggregate?.count ?? 0,
          occupied_stores: b.occupied_stores?.aggregate?.count ?? 0,
        });
      });
    }

    const myLists: ListSummary[] = Array.from(myGrouped.entries()).map(([list_name, d]) => ({
      list_id: d.list_id,
      list_name,
      description: d.description,
      list_type: d.list_type,
      count: d.items.length,
      items: d.items,
      sharedCount: sharedCountMap.get(list_name) || 0,
      org_access: (d.org_access as 'view' | 'edit' | null) || null,
      ...(d.list_type === 'store' && storeDetailsMap ? { storeDetails: storeDetailsMap } : {}),
      ...(d.list_type === 'brand' && brandDetailsMap ? { brandDetails: brandDetailsMap } : {}),
    }));

    if (storeDetailsMap) {
      sharedLists = sharedLists.map(l =>
        l.list_type === 'store' ? { ...l, storeDetails: storeDetailsMap } : l
      );
    }
    if (brandDetailsMap) {
      sharedLists = sharedLists.map(l =>
        l.list_type === 'brand' ? { ...l, brandDetails: brandDetailsMap } : l
      );
    }

    return { myLists, sharedLists };
  } catch (error) {
    console.error('Error fetching lists:', error);
    return { myLists: [], sharedLists: [] };
  }
};

export const fetchSavedBrandNames = async (): Promise<Set<string>> => {
  try {
    const userId = getUserId();
    const data = await graphqlFetch(`
      query GetSavedBrands($userId: uuid!) {
        app_list(where: { type: { _eq: "brand" }, brand_name: { _neq: "__placeholder__" }, user_id: { _eq: $userId } }) {
          brand_name
        }
      }
    `, { userId });

    const names = new Set<string>();
    (data.app_list || []).forEach((item: { brand_name: string | null }) => {
      if (item.brand_name) names.add(item.brand_name);
    });
    return names;
  } catch (error) {
    console.error('Error fetching saved brands:', error);
    return new Set();
  }
};

export const fetchSavedStorePoiIds = async (): Promise<Set<string>> => {
  try {
    const userId = getUserId();
    const data = await graphqlFetch(`
      query GetSavedStores($userId: uuid!) {
        app_list(where: { type: { _eq: "store" }, brand_name: { _neq: "__placeholder__" }, user_id: { _eq: $userId } }) {
          poi_id
        }
      }
    `, { userId });

    const ids = new Set<string>();
    (data.app_list || []).forEach((item: { poi_id: string | null }) => {
      if (item.poi_id) ids.add(item.poi_id);
    });
    return ids;
  } catch (error) {
    console.error('Error fetching saved stores:', error);
    return new Set();
  }
};

export interface FullStorePoi {
  id: string;
  site: string;
  address: string;
  brand_name: string;
  customer_id: string | null;
  channel: string | null;
  zipcode: string | null;
  latitude: number | null;
  longitude: number | null;
  purchek_install_date: string | null;
  containment_install_date: string | null;
  cart_manager_install_date: string | null;
  subscription_start_date: string | null;
  paired_wheel_install_date: string | null;
  first_purchase: string | null;
  last_purchase: string | null;
  lifetime_value: number | null;
  geo: {
    city: string;
    state_code: string;
    state: string;
    county: string;
    zipcode: string;
    total_crime_incidents: number;
    crime_rate: string;
    no_of_hot_days: number;
    no_of_cold_days: number;
    penalty_for_cart_owner: boolean;
    law_summary: string;
    law_source: string;
  } | null;
  nearby_penetration: {
    purchek_customer_count: number;
    penetration_purchek: number;
    containment_customer_count: number;
    penetration_containment: number;
    penetration_paired_wheel: number;
    plaza_cm_customer_count: number;
    penetration: number;
    shoplifting_amount_usd: number;
    shoplifting_incident_count: number;
  } | null;
}

const FULL_STORE_POI_FIELDS = `
  id
  site
  address
  brand_name
  customer_id
  channel
  zipcode
  latitude
  longitude
  purchek_install_date
  containment_install_date
  cart_manager_install_date
  subscription_start_date
  paired_wheel_install_date
  first_purchase
  last_purchase
  lifetime_value
  geo {
    city
    state_code
    state
    county
    zipcode
    total_crime_incidents
    crime_rate
    no_of_hot_days
    no_of_cold_days
    penalty_for_cart_owner
    law_summary
    law_source
  }
  nearby_penetration {
    purchek_customer_count
    penetration_purchek
    containment_customer_count
    penetration_containment
    penetration_paired_wheel
    plaza_cm_customer_count
    penetration
    shoplifting_amount_usd
    shoplifting_incident_count
    highway_proximity
    total_density
  }
  video {
    total_crime_amount_usd
    incident_count
  }
  store_notes(order_by: {created_at: desc}) {
    id
    comment
    created_at
    updated_at
    user {
      id
      name
    }
  }
  updated_store_ids(order_by: {created_at: desc}, limit: 1) {
    store_id
    created_at
  }
`;

export const fetchListById = async (listId: string): Promise<{ list_name: string; description: string | null; list_type: string; org_access: string | null; user_id: string; shared_permission?: 'view' | 'edit' | null } | null> => {
  try {
    const userId = getUserId();
    const data = await graphqlFetch(`
      query GetListById($id: uuid!) {
        app_list_by_pk(id: $id) {
          list_name
          description
          list_type
          org_access
          user_id
        }
      }
    `, { id: listId });

    const listInfo = data.app_list_by_pk;
    if (!listInfo) return null;

    // If current user is not the owner, check their shared permission
    let sharedPermission: 'view' | 'edit' | null = null;
    if (listInfo.user_id !== userId) {
      try {
        const shareData = await graphqlFetch(`
          query GetMySharePermission($listName: String!, $userId: uuid!) {
            app_shared_list(where: { list_name: { _eq: $listName }, shared_with: { _eq: $userId } }, limit: 1) {
              permission
            }
          }
        `, { listName: listInfo.list_name, userId });
        const entry = shareData.app_shared_list?.[0];
        sharedPermission = entry?.permission || null;
      } catch {
        // ignore
      }
    }

    return {
      ...listInfo,
      shared_permission: sharedPermission,
    };
  } catch (error) {
    console.error('Error fetching list by id:', error);
    return null;
  }
};

export const fetchListStoresFull = async (listName: string): Promise<FullStorePoi[]> => {
  try {
    const data = await graphqlFetch(`
      query GetListStorePoiIds($listName: String!) {
        app_list(where: {
          list_name: { _eq: $listName },
          type: { _eq: "store" },
          brand_name: { _neq: "__placeholder__" }
        }) {
          poi_id
        }
      }
    `, { listName });

    const poiIds = (data.app_list || [])
      .map((item: { poi_id: string | null }) => item.poi_id)
      .filter(Boolean) as string[];

    if (poiIds.length === 0) return [];

    const poiData = await graphqlFetch(`
      query GetFullStoreDetails($poiIds: [String!]!) {
        udm_poi(where: { id: { _in: $poiIds }, closed_on: { _is_null: true } }) {
          ${FULL_STORE_POI_FIELDS}
        }
      }
    `, { poiIds });

    return poiData.udm_poi || [];
  } catch (error) {
    console.error('Error fetching list stores:', error);
    return [];
  }
};

export const fetchListItems = async (listName: string): Promise<ListItem[]> => {
  try {
    const data = await graphqlFetch(`
      query GetListItems($listName: String!) {
        app_list(where: { list_name: { _eq: $listName }, brand_name: { _neq: "__placeholder__" } }, order_by: { created_at: desc }) {
          id
          list_name
          description
          list_type
          type
          poi_id
          brand_name
          user_id
          created_at
          updated_at
        }
      }
    `, { listName });

    return data.app_list || [];
  } catch (error) {
    console.error('Error fetching list items:', error);
    return [];
  }
};

export const addToList = async (
  listName: string,
  type: 'brand' | 'store',
  poiId?: string | null,
  brandName?: string | null,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const userId = getUserId();
    await graphqlFetch(`
      mutation AddToList($list_name: String!, $type: String!, $list_type: String!, $poi_id: String, $brand_name: String, $user_id: uuid!) {
        insert_app_list_one(object: {
          list_name: $list_name,
          type: $type,
          list_type: $list_type,
          poi_id: $poi_id,
          brand_name: $brand_name,
          user_id: $user_id
        }) {
          id
        }
      }
    `, {
      list_name: listName,
      type,
      list_type: type,
      poi_id: poiId || null,
      brand_name: brandName || null,
      user_id: userId,
    });

    return { success: true };
  } catch (error: any) {
    const msg = error?.message || '';
    if (msg.includes('Uniqueness violation') || msg.includes('unique constraint')) {
      return { success: false, error: 'Item already exists in this list.' };
    }
    console.error('Error adding to list:', error);
    return { success: false, error: 'Failed to add item to list.' };
  }
};

export const bulkAddToList = async (
  listNames: string[],
  items: { poiId: string; brandName?: string | null }[],
  type: 'brand' | 'store' = 'store',
): Promise<{ success: boolean; insertedCount: number; error?: string }> => {
  try {
    const userId = getUserId();
    const objects: any[] = [];
    for (const listName of listNames) {
      for (const item of items) {
        objects.push({
          list_name: listName,
          type,
          list_type: type,
          poi_id: item.poiId || null,
          brand_name: item.brandName || null,
          user_id: userId,
        });
      }
    }

    const data = await graphqlFetch(`
      mutation BulkAddToList($objects: [app_list_insert_input!]!) {
        insert_app_list(
          objects: $objects,
          on_conflict: { constraint: unique_list_entry, update_columns: [] }
        ) {
          affected_rows
        }
      }
    `, { objects });

    const insertedCount = data?.insert_app_list?.affected_rows ?? 0;
    return { success: true, insertedCount };
  } catch (error: any) {
    console.error('Error bulk adding to list:', error);
    return { success: false, insertedCount: 0, error: 'Failed to add items to list.' };
  }
};

export const removeFromList = async (id: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const userId = getUserId();
    const data = await graphqlFetch(`
      mutation RemoveFromList($id: uuid!, $userId: uuid!) {
        delete_app_list(where: { id: { _eq: $id }, user_id: { _eq: $userId } }) {
          affected_rows
        }
      }
    `, { id, userId });

    const affected = data?.delete_app_list?.affected_rows ?? 0;
    if (affected === 0) {
      return { success: false, error: 'Item not found or no permission to delete.' };
    }
    return { success: true };
  } catch (error) {
    console.error('Error removing from list:', error);
    return { success: false, error: 'Failed to remove item.' };
  }
};

export const removeStoresFromList = async (listName: string, poiIds: string[]): Promise<{ success: boolean; removedCount: number; error?: string }> => {
  try {
    const userId = getUserId();
    const data = await graphqlFetch(`
      mutation RemoveStoresFromList($listName: String!, $poiIds: [String!]!, $userId: uuid!) {
        delete_app_list_comment(where: {
          list_name: { _eq: $listName },
          poi_id: { _in: $poiIds }
        }) {
          affected_rows
        }
        delete_app_list(where: {
          list_name: { _eq: $listName },
          poi_id: { _in: $poiIds },
          user_id: { _eq: $userId }
        }) {
          affected_rows
        }
      }
    `, { listName, poiIds, userId });

    const affected = data?.delete_app_list?.affected_rows ?? 0;
    return { success: true, removedCount: affected };
  } catch (error) {
    console.error('Error removing stores from list:', error);
    return { success: false, removedCount: 0, error: 'Failed to remove stores.' };
  }
};

export const deleteList = async (listName: string): Promise<{ success: boolean; error?: string }> => {
  try {
    await graphqlFetch(`
      mutation DeleteList($listName: String!) {
        delete_app_list_comment(where: { list_name: { _eq: $listName } }) {
          affected_rows
        }
        delete_app_list(where: { list_name: { _eq: $listName } }) {
          affected_rows
        }
      }
    `, { listName });

    return { success: true };
  } catch (error) {
    console.error('Error deleting list:', error);
    return { success: false, error: 'Failed to delete list.' };
  }
};

export const renameList = async (oldName: string, newName: string): Promise<{ success: boolean; error?: string }> => {
  try {
    await graphqlFetch(`
      mutation RenameList($oldName: String!, $newName: String!) {
        update_app_list(
          where: { list_name: { _eq: $oldName } },
          _set: { list_name: $newName }
        ) {
          affected_rows
        }
      }
    `, { oldName, newName });

    return { success: true };
  } catch (error) {
    console.error('Error renaming list:', error);
    return { success: false, error: 'Failed to rename list.' };
  }
};

export const searchUsers = async (query: string): Promise<ShareUser[]> => {
  try {
    const data = await graphqlFetch(`
      query SearchUsers($query: String!) {
        app_user(
          where: {
            _and: [
              { email: { _like: "%gatekeepersystems.com" } },
              { _or: [
                { name: { _ilike: $query } },
                { email: { _ilike: $query } }
              ]}
            ]
          },
          limit: 10
        ) {
          id
          name
          email
        }
      }
    `, { query: `%${query}%` });

    return (data.app_user || []).filter((u: ShareUser) => u.id !== getUserId());
  } catch (error) {
    console.error('Error searching users:', error);
    return [];
  }
};

export const shareList = async (listName: string, sharedWithId: string, permission: 'view' | 'edit' = 'view'): Promise<{ success: boolean; error?: string }> => {
  try {
    const sharedBy = getUserId();
    await graphqlFetch(`
      mutation ShareList($list_name: String!, $shared_by: uuid!, $shared_with: uuid!, $permission: String!) {
        insert_app_shared_list_one(object: {
          list_name: $list_name,
          shared_by: $shared_by,
          shared_with: $shared_with,
          permission: $permission
        }) {
          id
        }
      }
    `, { list_name: listName, shared_by: sharedBy, shared_with: sharedWithId, permission });

    return { success: true };
  } catch (error: any) {
    const msg = error?.message || '';
    if (msg.includes('Uniqueness violation') || msg.includes('unique constraint')) {
      return { success: false, error: 'List already shared with this user.' };
    }
    console.error('Error sharing list:', error);
    return { success: false, error: 'Failed to share list.' };
  }
};

export const fetchSharedUsers = async (listName: string): Promise<SharedEntry[]> => {
  try {
    const data = await graphqlFetch(`
      query GetSharedUsers($listName: String!) {
        app_shared_list(where: { list_name: { _eq: $listName } }, order_by: { created_at: desc }) {
          id
          shared_with
          permission
          shared_with_user {
            id
            name
            email
          }
          created_at
        }
      }
    `, { listName });

    return data.app_shared_list || [];
  } catch (error) {
    console.error('Error fetching shared users:', error);
    return [];
  }
};

export const unshareList = async (shareId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    await graphqlFetch(`
      mutation UnshareList($id: uuid!) {
        delete_app_shared_list_by_pk(id: $id) {
          id
        }
      }
    `, { id: shareId });

    return { success: true };
  } catch (error) {
    console.error('Error unsharing list:', error);
    return { success: false, error: 'Failed to remove share.' };
  }
};

export const updateSharePermission = async (shareId: string, permission: 'view' | 'edit'): Promise<{ success: boolean; error?: string }> => {
  try {
    await graphqlFetch(`
      mutation UpdateSharePermission($id: uuid!, $permission: String!) {
        update_app_shared_list_by_pk(pk_columns: { id: $id }, _set: { permission: $permission }) {
          id
        }
      }
    `, { id: shareId, permission });

    return { success: true };
  } catch (error) {
    console.error('Error updating share permission:', error);
    return { success: false, error: 'Failed to update permission.' };
  }
};

export const updateOrgAccess = async (listName: string, orgAccess: 'view' | 'edit' | null): Promise<{ success: boolean; error?: string }> => {
  try {
    await graphqlFetch(`
      mutation UpdateOrgAccess($listName: String!, $orgAccess: String) {
        update_app_list(
          where: { list_name: { _eq: $listName } },
          _set: { org_access: $orgAccess }
        ) {
          affected_rows
        }
      }
    `, { listName, orgAccess });

    return { success: true };
  } catch (error) {
    console.error('Error updating org access:', error);
    return { success: false, error: 'Failed to update organization access.' };
  }
};

/**
 * Fetch user-specific notes count per brand + saved brand names in a single call.
 * Always fetches fresh data (no cache) so it reflects the current user's state.
 */
export interface UserBrandMeta {
  notesCountMap: Map<string, number>;
  savedBrandsSet: Set<string>;
}

// ── List Comments ──

export interface ListComment {
  id: string;
  list_name: string;
  poi_id: string | null;
  parent_id: string | null;
  user_id: string;
  comment: string;
  created_at: string;
  updated_at: string;
  user: { id: string; name: string | null; email: string };
  replies: ListComment[];
}

export const fetchListComments = async (listName: string): Promise<ListComment[]> => {
  try {
    const data = await graphqlFetch(`
      query GetListComments($listName: String!) {
        app_list_comment(
          where: { list_name: { _eq: $listName }, parent_id: { _is_null: true } },
          order_by: { created_at: desc }
        ) {
          id
          list_name
          poi_id
          parent_id
          user_id
          comment
          created_at
          updated_at
          user { id name email }
          replies(order_by: { created_at: asc }) {
            id
            list_name
            poi_id
            parent_id
            user_id
            comment
            created_at
            updated_at
            user { id name email }
            replies { id }
          }
        }
      }
    `, { listName });
    return data.app_list_comment || [];
  } catch (error) {
    console.error('Error fetching list comments:', error);
    return [];
  }
};

export const addListComment = async (
  listName: string,
  commentText: string,
  poiId?: string | null,
  parentId?: string | null,
): Promise<{ success: boolean; comment?: ListComment; error?: string }> => {
  try {
    const userId = getUserId();
    const data = await graphqlFetch(`
      mutation AddListComment($list_name: String!, $comment: String!, $poi_id: String, $parent_id: uuid, $user_id: uuid!) {
        insert_app_list_comment_one(object: {
          list_name: $list_name,
          comment: $comment,
          poi_id: $poi_id,
          parent_id: $parent_id,
          user_id: $user_id
        }) {
          id
          list_name
          poi_id
          parent_id
          user_id
          comment
          created_at
          updated_at
          user { id name email }
          replies { id }
        }
      }
    `, {
      list_name: listName,
      comment: commentText,
      poi_id: poiId || null,
      parent_id: parentId || null,
      user_id: userId,
    });
    return { success: true, comment: data.insert_app_list_comment_one };
  } catch (error) {
    console.error('Error adding comment:', error);
    return { success: false, error: 'Failed to add comment.' };
  }
};

export const deleteListComment = async (commentId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    await graphqlFetch(`
      mutation DeleteListComment($id: uuid!) {
        delete_app_list_comment_by_pk(id: $id) { id }
      }
    `, { id: commentId });
    return { success: true };
  } catch (error) {
    console.error('Error deleting comment:', error);
    return { success: false, error: 'Failed to delete comment.' };
  }
};

export const fetchUserBrandMeta = async (): Promise<UserBrandMeta> => {
  try {
    const userId = getUserId();
    const data = await graphqlFetch(`
      query GetUserBrandMeta($userId: uuid!) {
        user_notes: app_brand_notes(where: { user_id: { _eq: $userId } }) {
          brand_name
        }
        saved_brands: app_list(where: {
          user_id: { _eq: $userId },
          type: { _eq: "brand" },
          brand_name: { _neq: "__placeholder__" }
        }) {
          brand_name
        }
      }
    `, { userId });

    const notesCountMap = new Map<string, number>();
    (data.user_notes || []).forEach((note: { brand_name: string }) => {
      if (note.brand_name) {
        notesCountMap.set(note.brand_name, (notesCountMap.get(note.brand_name) || 0) + 1);
      }
    });

    const savedBrandsSet = new Set<string>();
    (data.saved_brands || []).forEach((item: { brand_name: string | null }) => {
      if (item.brand_name) savedBrandsSet.add(item.brand_name);
    });

    return { notesCountMap, savedBrandsSet };
  } catch (error) {
    console.error('Error fetching user brand meta:', error);
    return { notesCountMap: new Map(), savedBrandsSet: new Set() };
  }
};
